import React, { memo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { createUseStyles, Loader } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from 'lib/Web5Context';
import useLoadingPhrases from 'shards/Copilot/hooks/useLoadingPhrases';

import styles from './styles';

const useStyles = createUseStyles(styles, 'CopilotBarLoading');

/**
 * CopilotBarLoading Component
 *
 * Displays the latest incoming loading phrase while chat is processing.
 * The loader remains visible for the whole loading phase.
 *
 * @param {node} [doneContent='Done!'] - Content to display when loading completes
 * @param {string} [initialLoadingPhrase] - Optional phrase to always show first when loading starts
 * @param {boolean} isLoading - Whether the loading state is active
 * @param {string} [loadingPhrase] - Latest loading phrase received while loading
 */
const CopilotBarLoading = memo(
    ({ doneContent, initialLoadingPhrase, isLoading, loadingPhrase }) => {
        const classes = useStyles();
        const { t } = useWeb5Context();

        const currentPhrase = useLoadingPhrases({
            initialPhrase: initialLoadingPhrase,
            isLoading,
            loadingPhrase,
            stripMarkdown: true,
            stripEmoji: true,
        });

        const displayText = isLoading
            ? currentPhrase
            : (doneContent ?? t('label_copilot_bar_default_done'));

        return (
            <div className={classes.container}>
                <div className={classes.phraseRow}>
                    <div
                        className={classnames(classes.phrase, {
                            [classes.phraseDone]: !isLoading,
                            [classes.phraseLoading]: isLoading,
                        })}
                    >
                        {isLoading && <Loader className={classes.loader} size="tiny" />}
                        <div className={classes.phraseText}>{displayText ?? '\u00A0'}</div>
                    </div>
                </div>
            </div>
        );
    },
);

CopilotBarLoading.displayName = 'CopilotBarLoading';

CopilotBarLoading.propTypes = {
    doneContent: PropTypes.node,
    initialLoadingPhrase: PropTypes.string,
    isLoading: PropTypes.bool.isRequired,
    loadingPhrase: PropTypes.string,
};

export default CopilotBarLoading;
